import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { SummaryPlayWrapper } from './SummaryPlay.style';
import SummaryPlayEvents from './play/SummaryPlayEvents';
import SingleAdDataContainer from '../Ads/containers/SingleAdDataContainer';
import preGameList from '@gameday/utils/preGameList';
import FirstPitchDataContainer from '../FirstPitch/FirstPitchDataContainer';
import { determineSummaryEventDisplay } from './play/utils/displayHelpers';
import filterList from '../Summary/config/filter';
import { PlayEventTypes } from './play/PlayEvents';

const {
    PICKOFF,
    STEPOFF
} = PlayEventTypes;

const TOP = 'top';
const SUMMARY_TOP = 'Summary tab';

const getVideoContext = (filters) => {
    if (!filters) {
        return SUMMARY_TOP;
    }

    for (const key of Object.keys(filters)) {
        if (key && filters[key] && filterList[key]?.name) {
            return SUMMARY_TOP + ' : ' + filterList[key]?.name;
        }
    }

    return SUMMARY_TOP;
};

// Determine if a specific Play Result should be shown.
export const determineResultDisplay = (
    result,
    about,
    resultVideo,
    filterMap = { all: true },
    isScoringAtBat,
    atBatIsKeyMoment,
    isStrikeout,
    isHardHit,
    isHomeRun,
    isTieOrLeadChangePlay,
    atBatHasLargeWinProbChange
) => {
    if (result && about?.isComplete) {
        const {
            all,
            scoringPlays,
            highlights,
            keyMoments,
            homeRuns,
            strikeouts,
            hardHits,
            leadChange,
            winProb
        } = filterMap;

        return (all) ||
            (scoringPlays && isScoringAtBat) ||
            (highlights && resultVideo) ||
            (keyMoments && atBatIsKeyMoment) ||
            (strikeouts && isStrikeout) ||
            (hardHits && isHardHit) ||
            (homeRuns && isHomeRun) ||
            (leadChange && isTieOrLeadChangePlay) ||
            (winProb && atBatHasLargeWinProbChange);
    }
    return false;
};
export const showStatcast = (filterMap = { all: true }, isHomeRun) => {
    const {
        keyMoments,
        homeRuns,
        hardHits
    } = filterMap;
    return homeRuns || hardHits || (keyMoments && isHomeRun);
};

export const showWinPercent = (filterMap = { all: true }) => {
    const {
        keyMoments,
        winProb
    } = filterMap;
    return keyMoments || winProb;
};

export const showSituationInfo = (filterMap = { all: true }) => {
    const {
        keyMoments
    } = filterMap;
    return keyMoments;
};

// Build the total number of segments of the play to display
const buildSummaryPlaySegments = (
    playData,
    teams,
    resultVideo,
    filterMap,
    isLoading,
    deviceInteraction,
    openAtBatOverlay,
    playMetrics = {},
    displayParallaxAd,
    language
) => {
    const {
        playEvents = [],
        about = {},
        result = {},
        matchup = {},
        count = {},
        atBatIndex
    } = playData;

    const {
        batter = {}
    } = matchup;

    const playSegments = [];

    const pitches = playData.pitchIndex || [];

    let index = 0;

    const {
        isStrikeout,
        isHardHit,
        isScoringAtBat,
        atBatIsKeyMoment,
        atBatHasLargeWinProbChange,
        atBatIsTieOrLeadChange,
        leadChangePlayEvents,
        isHomeRun,
        halfInning,
        playEvents: pmEvents
    } = playMetrics;

    const leadChangeMap = {};
    leadChangePlayEvents?.length && leadChangePlayEvents.forEach((index) => {
        leadChangeMap[index] = true;
    });

    const lastPEIndex = playEvents.length - 1;

    for (const event of playEvents) {
        const {
            details,
            type
        } = event;
        const pmEventData = pmEvents && pmEvents[index] || {};
        // Flags for determining if we should show the EVENT based on the filters
        const eventFlags = {
            isScoringEvent: details?.isScoringPlay,
            isKeyMomentEvent: pmEventData.exceedsKMWpaThreshold,
            isWinProbEvent: pmEventData.exceedsWpaThreshold,
            isLeadChangeEvent: leadChangeMap[index]
        };
        if (determineSummaryEventDisplay(filterMap, event, eventFlags) && index < lastPEIndex) {
            const {
                event: e,
                eventType,
                description,
                isScoringPlay,
                homeScore,
                awayScore,
                isOut
            } = details;

            // Determine if the playEvent resulted in an out.
            // Similar to above's logic, this is faulty
            const thisPitch = pitches && playEvents[index];
            const thisPitchDetails = thisPitch && thisPitch.details;
            const iconName = (type === PICKOFF || type === STEPOFF) ? type : eventType;
            let isPreGameEvent = false;
            let isPlayEventFirstPitch = false;

            if (atBatIndex === 0) { // to identify the first atbat
                if (thisPitchDetails && thisPitchDetails.eventType === 'game_advisory') {
                    const pitchDescription = thisPitchDetails?.description || '';
                    isPreGameEvent = preGameList.includes(pitchDescription);
                    if (isPreGameEvent && index === 0) { // only need the first one from first atbat playEvents
                        isPlayEventFirstPitch = true;
                    }
                }
            }

            const TempCom = !isPreGameEvent
                ? <SummaryPlayEvents
                    event={ e }
                    teams={ teams }
                    description={ description }
                    outs={ isOut ? count?.outs : undefined }
                    homeScore={ homeScore }
                    awayScore={ awayScore }
                    isScoringPlay={ isScoringPlay }
                    isTopInning={ about.halfInning === TOP }
                    halfInning={ halfInning }
                    eventType={ eventType }
                    isLoading={ isLoading }
                    deviceInteraction={ deviceInteraction }
                    key={`summary-play-${index}-event`}
                    openAtBatOverlay={openAtBatOverlay}
                    atBatIndex={atBatIndex}
                    eventIcon={ iconName }
                    showWinProb={ showWinPercent(filterMap) }
                    keymoments={ pmEventData }
                    language={language}
                />
                : isPlayEventFirstPitch ? <FirstPitchDataContainer iconName={'baseball'} showImage={true} key={`first-pitch-${index}-event`}/> : null;

            if (TempCom !== null) {
                playSegments.push(TempCom);
            }
        }
        // If last play event index, show play result also (if desired)
        const showResult = determineResultDisplay(
            result,
            about,
            resultVideo,
            filterMap,
            isScoringAtBat,
            atBatIsKeyMoment,
            isStrikeout,
            isHardHit,
            isHomeRun,
            atBatIsTieOrLeadChange,
            atBatHasLargeWinProbChange
        );
        if (index === lastPEIndex && showResult) {
            const {
                event,
                homeScore,
                awayScore,
                description,
                isOut
            } = result;

            const {
                hitData
            } = playEvents[index];

            const videoContextLabel = getVideoContext(filterMap);

            playSegments.push(
                <SummaryPlayEvents
                    isResult
                    playerId={ batter?.id }
                    event={ event }
                    teams={ teams }
                    description={ description }
                    outs={ isOut ? count?.outs : undefined }
                    homeScore={ homeScore }
                    awayScore={ awayScore }
                    isScoringPlay={ isScoringAtBat }
                    showWinProb={ showWinPercent(filterMap) }
                    keymoments={ playMetrics }
                    halfInning={ halfInning }
                    hitData={ hitData }
                    showHitData={ showStatcast(filterMap, isHomeRun) }
                    resultVideo={ resultVideo }
                    isTopInning={ halfInning === TOP }
                    isLoading={ isLoading }
                    deviceInteraction={ deviceInteraction }
                    key={`summary-play-${index}-result`}
                    openAtBatOverlay={openAtBatOverlay}
                    atBatIndex={atBatIndex}
                    showSituationInfo={ showSituationInfo(filterMap) }
                    playerContext={videoContextLabel}
                    language={language}
                />
            );
        }
        index++;
    }

    if (displayParallaxAd) {
        playSegments.push(<SingleAdDataContainer type='parallax' key={`single-ad-${index}-event`}/>);
    }

    return playSegments;
};

const SummaryPlay = ({
    playData,
    teams,
    resultVideo,
    isLoading,
    deviceInteraction = {},
    filters = {
        all: true
    },
    openAtBatOverlay,
    playMetrics,
    displayParallaxAd,
    language
}) => {
    const summaryPlaySegments = useMemo(() => buildSummaryPlaySegments(
        playData,
        teams,
        resultVideo,
        filters,
        isLoading,
        deviceInteraction,
        openAtBatOverlay,
        playMetrics,
        displayParallaxAd,
        language
    ), [playData, isLoading, filters, playMetrics, language]);

    return (
        <SummaryPlayWrapper>
            { summaryPlaySegments }
        </SummaryPlayWrapper>
    );
};

SummaryPlay.propTypes = {
    playData: PropTypes.object.isRequired,
    teams: PropTypes.object.isRequired,
    resultVideo: PropTypes.object,
    isLoading: PropTypes.bool,
    deviceInteraction: PropTypes.object,
    filters: PropTypes.object,
    openAtBatOverlay: PropTypes.func,
    displayParallaxAd: PropTypes.bool,
    language: PropTypes.string
};

export default SummaryPlay;
